import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

import { DateTime } from "luxon";
import { webinars, syncState } from "@/plugins/LocalForagePlugin";
import { webinarsSyncKey } from "@/types/products/product-definitions";
import type {
    ArticleMetaData,
    Author,
    Category,
    ContentfulWebinar,
    HasMetaData,
    IndustrySector,
    ContentfulSector,
    Product,
    SearchableTicker,
    Tag,
    Webinar,
    WebinarMetaData
} from "@tmi/types/entities";

import type { Webinars } from "@tmi/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class WebinarsSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {
            const syncThresholdInMillis = await syncState.getItem< number >( webinarsSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
            {
                logger.debug( `[ webinars-sync-coordinator ] load all available webinar meta-data...` );
                const t0: number = performance.now();

                // Retrieve & store the latest meta-data for webinars
                const allMetadata = await this.refreshMetaDataFromServer( authToken );

                if ( 0 === allMetadata.length ) {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( webinarsSyncKey, nextSyncThresholdInMillis );

                    return itemsUpdated;
                }

                // Set throttle after successful fetch
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( webinarsSyncKey, nextSyncThresholdInMillis );

                const t1: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } webinar meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map< string, ArticleMetaData >(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                // Manage all cached webinars
                const t2: number = performance.now();

                if ( await webinars.length() > 0 ) {
                    const keysToRemove: string[] = [];

                    // Iterate over all elements within IDB
                    await webinars.iterate( ( content: Webinar, key: string ) => {
                        const meta: WebinarMetaData | undefined = allMetaDataByIdMap.get( key );
                        if ( typeof meta !== 'undefined' ) {
                            if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                                allMetaDataByIdMap.delete( key );
                            } else {
                                keysToRemove.push( key );
                            }
                        } else {
                            keysToRemove.push( key );
                        }
                        return;
                    } );

                    for ( const key of keysToRemove ) {
                        await webinars.removeItem( key );
                    }
                }

                const t3: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Aggregated missing webinars in ${ t3 - t2 } ms` );

                // Transform metadata map into an array of content ids
                const missingIds = [ ...Array.from( allMetaDataByIdMap.keys() ) ];
                // If any content is missing...
                if ( missingIds.length > 0 ) {
                    // Fetch it from the server
                    itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
                }

                const t4: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Processing all missing webinars took ${ t4 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( '[ webinars-sync-coordinator ] Sync failed:', err );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< WebinarMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: WebinarMetaData[] | undefined = [];
        let metaPayload: ContentfulWebinar[] | undefined = [];

        do {
            const response =
                await goFetch< Webinars, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '3a47c3a294c1f35253d45a0df29a4fb5',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                fromPublishedDateTimeUTC: '2008-01-01'
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Webinar metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping webinar metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.webinarCatalog?.webinars;
            if ( metaPayload ) {
                metaPayload.forEach( ( a: ContentfulWebinar ) => {
                    metadata.push( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at,
                    } );
                } );
            }

            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        let page: number = 0;
        const pageSize: number = 1000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        let contentPayload: ContentfulWebinar[] | undefined = [];
        do {
            const response =
                await goFetch< Webinars, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '61a211b1eb8a5b94eb24bbcb79236ce6',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                ids: missingContentIds
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Webinar content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping webinar content pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            contentPayload = response?.data?.webinarCatalog?.webinars;
            if ( null != contentPayload ) {
                const allWebinars: Webinar[] = [];
                contentPayload.forEach( ( w: ContentfulWebinar ) => {
                    const webinar: Webinar | undefined = composeWebinar( w, this.#compositionContext );
                    if ( typeof webinar !== 'undefined' ) {
                        allWebinars.push( webinar )
                    }
                } );

                // Store the items recently retrieved
                await webinars.ready();
                try {
                    await Promise.all( allWebinars.map( w => webinars.setItem( w.contentfulId, w ) ) );
                } catch ( err: any ) {
                    logger.error( '[ webinars-sync-coordinator ] Sync failed:', err )
                }

                const t1: number = performance.now();
                logger.info( `[ webinars-sync-coordinator ] Processing ${ allWebinars.length } webinars took ${ t1 - t0 } ms` );

                itemsUpdated += allWebinars.length;
            }

            // update the page #
            page++;
        } while ( (contentPayload?.length ?? 0) >= pageSize );

        return itemsUpdated;
    }
}

export function composeWebinar( webinar: ContentfulWebinar, ctx: CompositionContext ): Webinar | undefined {
    try {
        const author: Author | undefined = ctx.getAuthorById( webinar.author_id );

        const adProduct: Product | undefined = webinar.ad_product_id
            ? ctx.getProductById( webinar.ad_product_id ) : undefined;

        const categories: Category[] = [];
        webinar.assoc_category_ids?.forEach( ( id: string ) => {
            const category = ctx.getCategoryById( id );
            if ( category ) categories.push( category );
        } );

        const industrySectors: IndustrySector[] = [];
        webinar.assoc_ind_sector_ids?.forEach( ( id: string ) => {
            const industrySector = ctx.getIndustrySectorById( id );
            if ( industrySector ) industrySectors.push( industrySector );
        } );

        const products: Product[] = [];
        webinar.assoc_product_ids?.forEach( ( id: string ) => {
            const product = ctx.getProductById( id );
            if ( product ) {
                products.push( product );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        const sectors: ContentfulSector[] = [];
        webinar.assoc_sector_ids?.forEach( ( id: string ): void => {
            const sector = ctx.getSectorById( id );
            if ( sector ) sectors.push( sector );
        } );

        const tags: Tag[] = [];
        webinar.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag = ctx.getTagById( id );
            if ( tag ) tags.push( tag );
        } );

        const tickers: SearchableTicker[] = [];
        webinar.assoc_ticker_ids?.forEach( ( id: string ): void => {
            const ticker = ctx.getTickerById( id );
            if ( ticker ) tickers.push( ticker );
        } );

        return ( {
            contentfulId: webinar.id,
            webinarDate: webinar.publish_dt,
            publishedDate: webinar.publish_dt,

            updatedDate: webinar.publish_at,

            slug: webinar.slug,
            title: webinar.title,
            text: webinar.body,

            isFeatured: webinar.featured,

            editorialAd: webinar.editorial_ad ?? undefined,
            adProduct: adProduct,

            registrationUri: webinar.reg_link,
            videoLink: webinar.video,

            author: author,

            categories: categories,
            industrySectors: industrySectors,
            products: products,
            sectors: sectors,
            tags: tags,
            tickers: tickers,

            // Extraneous data attributes used to consolidate the very similar models
            body2: undefined,
            preview: undefined,
            thumbnail: undefined
        } );
    } catch ( e: any ) {
        logger.error( `[ webinars-sync-coordinator ] Failed to compose webinar '${ webinar.id }':`, e );
        return undefined;
    }
}